/**
 * SharePartnerJobsModal - Share upcoming partner jobs via WhatsApp and/or email.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  Email,
  PictureAsPdf,
  ShareOutlined,
  TableView,
  WhatsApp,
} from "@mui/icons-material";
import { Table, Tag } from "antd";
import dayjs from "dayjs";
import {
  getPartnerJobsSchedulePreview,
  sharePartnerJobsSchedule,
  downloadPartnerJobsScheduleCsv,
  downloadPartnerJobsSchedulePdf,
} from "../services/partnerService.js";
import {
  formatShareSuccessMessage,
  getActiveChannelLabels,
  getMissingChannelNotice,
  openPartnerJobsWhatsApp,
} from "../utils/partnerJobsShare.js";

const SharePartnerJobsModal = ({ open, partner, onClose, onShared }) => {
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [shareError, setShareError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!partner?.id) return;

    setLoading(true);
    setLoadError(null);
    setShareError(null);
    setDownloadError(null);

    try {
      const result = await getPartnerJobsSchedulePreview(partner.id);
      if (!result.success) {
        throw new Error(result.error || "Failed to load schedule preview");
      }
      setPreview(result.data);
    } catch (error) {
      setPreview(null);
      setLoadError(error.message || "Failed to load schedule preview");
    } finally {
      setLoading(false);
    }
  }, [partner?.id]);

  useEffect(() => {
    if (open && partner?.id) {
      loadPreview();
    } else {
      setPreview(null);
      setLoadError(null);
      setShareError(null);
      setDownloadError(null);
    }
  }, [open, partner?.id, loadPreview]);

  const channels = preview?.channels || {};
  const jobs = preview?.jobs || [];
  const activeChannels = getActiveChannelLabels(channels);
  const missingNotice = getMissingChannelNotice(channels);
  const canShare =
    (channels.whatsapp || channels.email) && jobs.length > 0 && !loading;
  const canDownload = jobs.length > 0 && !loading;

  const columns = useMemo(
    () => [
      {
        title: "Job ID",
        dataIndex: "jobId",
        key: "jobId",
        width: 120,
      },
      {
        title: "Move Date",
        dataIndex: "moveDate",
        key: "moveDate",
        width: 130,
        render: (value) =>
          value ? dayjs(value).format("ddd, D MMM YYYY") : "TBC",
      },
      {
        title: "Route",
        key: "route",
        render: (_, record) => (
          <div>
            <div>{record.pickup?.address || "N/A"}</div>
            <div style={{ color: "#999" }}>→ {record.delivery?.address || "N/A"}</div>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status) => {
          const color = status === "in_progress" ? "blue" : "green";
          return (
            <Tag color={color}>
              {status?.replace("_", " ").toUpperCase() || "UNKNOWN"}
            </Tag>
          );
        },
      },
    ],
    []
  );

  const handleDownloadCsv = async () => {
    if (!partner?.id || !canDownload) return;
    setDownloadingCsv(true);
    setDownloadError(null);
    try {
      const result = await downloadPartnerJobsScheduleCsv(
        partner.id,
        preview?.filters
      );
      if (!result.success) {
        throw new Error(result.error || "Failed to download CSV");
      }
    } catch (error) {
      setDownloadError(error.message || "Failed to download CSV");
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!partner?.id || !canDownload) return;
    setDownloadingPdf(true);
    setDownloadError(null);
    try {
      const result = await downloadPartnerJobsSchedulePdf(
        partner.id,
        preview?.filters
      );
      if (!result.success) {
        throw new Error(result.error || "Failed to download PDF");
      }
    } catch (error) {
      setDownloadError(error.message || "Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleShare = async () => {
    if (!partner?.id || !canShare) return;

    setSharing(true);
    setShareError(null);

    try {
      const result = await sharePartnerJobsSchedule(partner.id, preview?.filters);

      if (!result.success) {
        throw new Error(result.error || "Failed to share schedule");
      }

      if (result.data?.whatsapp) {
        openPartnerJobsWhatsApp(result.data.whatsapp);
      }

      onShared?.({
        partner,
        channelsUsed: result.data?.channelsUsed,
        message: formatShareSuccessMessage(result.data?.channelsUsed),
      });
      onClose();
    } catch (error) {
      setShareError(error.message || "Failed to share schedule");
    } finally {
      setSharing(false);
    }
  };

  if (!partner) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ShareOutlined />
          <span>Share upcoming jobs — {partner.name}</span>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {activeChannels.map((label) => (
                <Chip
                  key={label}
                  icon={label === "WhatsApp" ? <WhatsApp /> : <Email />}
                  label={label}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {activeChannels.length === 0 && (
                <Chip label="No channels available" color="warning" />
              )}
            </Box>

            {missingNotice && (
              <Alert severity={activeChannels.length ? "info" : "warning"} sx={{ mb: 2 }}>
                {missingNotice}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              Showing {jobs.length} booked/in-progress job{jobs.length === 1 ? "" : "s"}{" "}
              for the next 14 days. Email includes PDF + CSV attachments.
            </Alert>

            {jobs.length === 0 ? (
              <Alert severity="warning">
                No upcoming jobs allocated to this partner for the selected period.
              </Alert>
            ) : (
              <>
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      downloadingCsv ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <TableView />
                      )
                    }
                    onClick={handleDownloadCsv}
                    disabled={!canDownload || downloadingCsv || downloadingPdf}
                  >
                    Download CSV
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      downloadingPdf ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <PictureAsPdf />
                      )
                    }
                    onClick={handleDownloadPdf}
                    disabled={!canDownload || downloadingCsv || downloadingPdf}
                  >
                    Download PDF
                  </Button>
                </Box>

                <Table
                  columns={columns}
                  dataSource={jobs}
                  rowKey="jobId"
                  size="small"
                  pagination={jobs.length > 8 ? { pageSize: 8 } : false}
                  scroll={{ x: true }}
                />

                {preview?.whatsappMessage && channels.whatsapp && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      WhatsApp preview
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "inherit",
                        fontSize: "0.875rem",
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "grey.200",
                        borderRadius: 1,
                        p: 2,
                        m: 0,
                      }}
                    >
                      {preview.whatsappMessage}
                    </Box>
                  </Box>
                )}
              </>
            )}

            {shareError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {shareError}
              </Alert>
            )}
            {downloadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {downloadError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={!canShare || sharing}
          startIcon={sharing ? <CircularProgress size={16} color="inherit" /> : <ShareOutlined />}
        >
          {sharing
            ? "Sharing..."
            : activeChannels.length > 1
              ? "Share via WhatsApp + Email"
              : activeChannels[0] === "WhatsApp"
                ? "Share via WhatsApp"
                : activeChannels[0] === "Email"
                  ? "Share via Email"
                  : "Share"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePartnerJobsModal;
